import "./style.scss";

import { Box, CircularProgress } from "@mui/material";
import type { ChannelSnapshotData, IChannel } from "containers/Channels/types";
import type { TPeriod } from "store/periodStore";
import { PeriodStatus } from "store/types";

import type { SubscriptionTableRow } from "../../SubscriptionTable/types";
import SubscriptionManagementModalBody from "./SubscriptionManagementModalBody";
import SubscriptionManagementModalFooter from "./SubscriptionManagementModalFooter";
import SubscriptionManagementModalHeader from "./SubscriptionManagementModalHeader";

interface SubscriptionManagementModalUIProps {
    onClose: () => void;
    selectedPeriod: TPeriod;
    activePeriods: TPeriod[];
    subscriptionChannels?: IChannel[];
    channelId: number;
    channelSnapshot: ChannelSnapshotData | null;
    totalSubscriptionPoints: number;
    isLoadingSnapshot: boolean;
    hasCompositePeriods: boolean;
    tableData: SubscriptionTableRow[];
    tableIsLoading: boolean;
    tablePage: number;
    tableRowsPerPage: number;
    tableTotalCount: number;
    totalRevenue: string;
    changedRows: Map<number, { consumptionCount?: number; avgConsumptionDuration?: number }>;
    isTotalRevenueChanged: boolean;
    isProcessingJobInProgress: boolean;
    isLoadingComposites?: boolean;
    searchQuery: string;
    onTotalRevenueChange: (value: string) => void;
    onCellSave: (rowId: string | number, columnId: string, value: any) => void;
    onSave: () => Promise<void>;
    onTablePageChange: (event: unknown, newPage: number) => void;
    onTableRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onOpenImport?: () => void;
}

/**
 * Pure UI component for subscription management modal.
 * Displays the modal skeleton with header and footer always visible.
 * Body content shows loading state or actual data based on isLoadingComposites.
 */
const SubscriptionManagementModalUI = ({
    onClose,
    selectedPeriod,
    tableData,
    tableIsLoading,
    tablePage,
    tableRowsPerPage,
    tableTotalCount,
    totalRevenue,
    changedRows,
    isTotalRevenueChanged,
    channelSnapshot,
    totalSubscriptionPoints,
    isProcessingJobInProgress,
    isLoadingComposites = false,
    hasCompositePeriods,
    searchQuery,
    onTotalRevenueChange,
    onCellSave,
    onSave,
    onTablePageChange,
    onTableRowsPerPageChange,
    onSearchChange,
    onOpenImport,
}: SubscriptionManagementModalUIProps) => {
    // Check if selected period is not closed AND no processing job is in progress AND no composite periods (editable)
    const isEditable =
        selectedPeriod.isActive === PeriodStatus.CURRENTLY_OPEN && !isProcessingJobInProgress && !hasCompositePeriods;

    const hasChanges = changedRows.size > 0 || isTotalRevenueChanged;

    return (
        <div className="subscription-management-modal">
            <SubscriptionManagementModalHeader
                selectedPeriod={selectedPeriod}
                isProcessingJobInProgress={isProcessingJobInProgress}
                isEditable={isEditable}
                onOpenImport={onOpenImport}
            />

            {isLoadingComposites ? (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flex: 1,
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : (
                <SubscriptionManagementModalBody
                    isEditable={isEditable}
                    hasCompositePeriods={hasCompositePeriods}
                    tableData={tableData}
                    tableIsLoading={tableIsLoading}
                    tablePage={tablePage}
                    tableRowsPerPage={tableRowsPerPage}
                    tableTotalCount={tableTotalCount}
                    totalRevenue={totalRevenue}
                    changedRows={changedRows}
                    channelSnapshot={channelSnapshot}
                    totalSubscriptionPoints={totalSubscriptionPoints}
                    searchQuery={searchQuery}
                    onTotalRevenueChange={onTotalRevenueChange}
                    onCellSave={onCellSave}
                    onTablePageChange={onTablePageChange}
                    onTableRowsPerPageChange={onTableRowsPerPageChange}
                    onSearchChange={onSearchChange}
                />
            )}

            <SubscriptionManagementModalFooter
                onClose={onClose}
                onSave={onSave}
                hasChanges={hasChanges}
                isProcessingJobInProgress={isProcessingJobInProgress}
            />
        </div>
    );
};

export default SubscriptionManagementModalUI;
